import gql from 'graphql-tag';
import React from 'react';

import {formatMolecularFormula} from 'chemistry/molecules/MoleculeUtil';
import {MoleculeChemicalFormulaQuery} from 'chemistry/molecules/tabs/__generated/MoleculeChemicalFormulaQuery';
import {MoleculeFieldRow, MoleculeInaccessibleMessage} from 'chemistry/molecules/tabs/MoleculeStructureField';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const MOLECULE_CHEMICAL_FORMULA_QUERY = gql`
  query MoleculeChemicalFormulaQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            chemicalFormula {
              ... on ChemicalFormulaNode {
                value
              }
              ... on ErrorNode {
                message
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface MoleculeChemicalFormulaProps {
  moleculeId: string;
  moleculeVersionId: string;
}

interface ChemicalFormula {
  __typename: string;
  value: string;
}

interface ChemicalFormulaError {
  __typename: string;
  message: string;
}

function isError(
  chemicalFormula: ChemicalFormula | ChemicalFormulaError
): chemicalFormula is ChemicalFormulaError {
  return chemicalFormula.__typename === 'ErrorNode';
}

export default function MoleculeChemicalFormula(props: MoleculeChemicalFormulaProps): JSX.Element | null {
  return (
    <BenchlingQuery
      query={MOLECULE_CHEMICAL_FORMULA_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: MoleculeChemicalFormulaQuery) => {
        if (isInaccessible(data.molecule)) {
          return <MoleculeInaccessibleMessage />;
        }
        if (isError(data.molecule.version.chemicalStructure.chemicalFormula)) {
          return (
            <MoleculeFieldRow label="Chemical Formula">
              {data.molecule.version.chemicalStructure.chemicalFormula.message}
            </MoleculeFieldRow>
          );
        }
        return (
          <MoleculeFieldRow label="Chemical Formula">
            {formatMolecularFormula(data.molecule.version.chemicalStructure.chemicalFormula.value)}
          </MoleculeFieldRow>
        );
      }}
    </BenchlingQuery>
  );
}
